import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import {
  generateCorrectiveActionFromFinding,
  getFinding,
  reviewFinding,
} from '../../lib/services/findings'
import { getReport, releaseReport } from '../../lib/services/reports'
import {
  canAccessReviewerWorkbench,
  canGenerateCapaFromFinding,
  canReleaseDraftReportFromWorkbench,
  canTransitionFindingStatus,
} from '../../lib/services/reviewerWorkbench'

export function ReviewerDetailPage() {
  const { artifactType, artifactId } = useParams()
  const { viewerContext } = useAuth()
  const role = viewerContext?.role || 'Surveyor'
  const canAccess = canAccessReviewerWorkbench(role)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [generatedActionId, setGeneratedActionId] = useState<string | null>(null)
  const [isActing, setIsActing] = useState(false)

  const findingQuery = useQuery({
    queryKey: ['reviewer-finding-detail', artifactId],
    queryFn: () => getFinding(artifactId!),
    enabled: canAccess && artifactType === 'finding' && !!artifactId,
  })

  const reportQuery = useQuery({
    queryKey: ['reviewer-report-detail', artifactId],
    queryFn: () => getReport(artifactId!),
    enabled: canAccess && artifactType === 'report' && !!artifactId,
  })

  if (!canAccess) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        Reviewer detail is restricted to Reviewer/Admin roles.
      </div>
    )
  }

  if (artifactType !== 'finding' && artifactType !== 'report') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        Invalid reviewer artifact type.
      </div>
    )
  }

  if (artifactType === 'finding') {
    const finding = findingQuery.data
    if (findingQuery.isLoading) return <div className="p-10 text-sm text-slate-500">Loading finding review...</div>
    if (findingQuery.error || !finding) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-semibold">Unable to load finding</p>
          <p className="text-sm">{(findingQuery.error as Error | undefined)?.message || 'Finding not found'}</p>
        </div>
      )
    }

    const canApprove = canTransitionFindingStatus(role, finding.status, 'approved')
    const canReject = canTransitionFindingStatus(role, finding.status, 'rejected')
    const canGenerateCapa = canGenerateCapaFromFinding(role, finding.status)

    return (
      <div className="mx-auto max-w-4xl space-y-5">
        <header className="v11-workspace-header">
          <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Finding Review</h1>
          <p className="mt-1 text-sm text-slate-600">Standard {finding.standard_id} | Severity {finding.severity}</p>
          <span className={finding.status === 'draft' ? 'badge-in-progress mt-3 inline-flex' : 'badge-released mt-3 inline-flex'}>
            {finding.status}
          </span>
        </header>

        <section className="v11-section p-5">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Summary</h2>
          <p className="mt-2 text-sm text-slate-700">{finding.summary}</p>
        </section>

        <section className="v11-section p-5">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Reviewer Actions</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              className="btn-primary text-sm"
              disabled={isActing || !canApprove}
              onClick={async () => {
                setActionError(null)
                setActionSuccess(null)
                setIsActing(true)
                try {
                  await reviewFinding(finding.finding_id, 'approved')
                  setActionSuccess('Finding approved.')
                  await findingQuery.refetch()
                } catch (err) {
                  setActionError((err as Error).message)
                } finally {
                  setIsActing(false)
                }
              }}
            >
              Approve
            </button>
            <button
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isActing || !canReject}
              onClick={async () => {
                setActionError(null)
                setActionSuccess(null)
                setIsActing(true)
                try {
                  await reviewFinding(finding.finding_id, 'rejected')
                  setActionSuccess('Finding rejected.')
                  await findingQuery.refetch()
                } catch (err) {
                  setActionError((err as Error).message)
                } finally {
                  setIsActing(false)
                }
              }}
            >
              Reject
            </button>
            <button
              className="rounded-lg border border-[var(--brand-primary)] bg-white px-3 py-2 text-sm font-semibold text-[var(--brand-primary)] hover:bg-[var(--brand-light)] disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isActing || !canGenerateCapa}
              title="Create an open CAPA linked to this finding (admin/editor/superadmin)"
              onClick={async () => {
                setActionError(null)
                setActionSuccess(null)
                setGeneratedActionId(null)
                setIsActing(true)
                try {
                  const action = await generateCorrectiveActionFromFinding(finding.finding_id, {
                    evidence_required: finding.severity === 'high' || finding.severity === 'medium',
                  })
                  setGeneratedActionId(action.action_id)
                  setActionSuccess(`CAPA created (${action.action_id.slice(0, 8)}…).`)
                } catch (err) {
                  setActionError((err as Error).message)
                } finally {
                  setIsActing(false)
                }
              }}
            >
              Generate CAPA
            </button>
          </div>
          {(!canApprove || !canReject) && (
            <p className="mt-2 text-xs text-slate-500">
              Approve/reject is only available to Admin users when the finding is still in draft state.
            </p>
          )}
          {!canGenerateCapa && (
            <p className="mt-2 text-xs text-slate-500">
              Generate CAPA is available to Admin users for non-rejected findings (#1855).
            </p>
          )}
          {generatedActionId && (
            <p className="mt-3 text-sm">
              <Link
                to={`/actions/${generatedActionId}`}
                className="font-semibold text-[var(--brand-primary)] underline"
              >
                Open generated CAPA
              </Link>
            </p>
          )}
        </section>

        {actionError && <p className="text-sm text-red-600" role="alert">{actionError}</p>}
        {actionSuccess && <p className="text-sm text-emerald-700" role="status">{actionSuccess}</p>}
        <Link to="/reviewer" className="inline-block text-sm underline">
          Back to reviewer queue
        </Link>
      </div>
    )
  }

  const report = reportQuery.data
  if (reportQuery.isLoading) return <div className="p-10 text-sm text-slate-500">Loading report review...</div>
  if (reportQuery.error || !report) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        <p className="font-semibold">Unable to load report</p>
        <p className="text-sm">{(reportQuery.error as Error | undefined)?.message || 'Report not found'}</p>
      </div>
    )
  }

  const canRelease = canReleaseDraftReportFromWorkbench(role, report.status)

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <header className="v11-workspace-header">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Report Review</h1>
        <p className="mt-1 text-sm text-slate-600">Report {report.report_id} | Visit {report.visit_id}</p>
        <span className={report.status === 'draft' ? 'badge-in-progress mt-3 inline-flex' : 'badge-released mt-3 inline-flex'}>
          {report.status}
        </span>
      </header>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Release Readiness</h2>
        <p className="mt-2 text-sm text-slate-700">
          Draft reports are mutable and pending Admin release. Reviewer users can inspect readiness, but release remains
          restricted to Admin users. Released reports are immutable.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            className="btn-primary text-sm"
            disabled={isActing || !canRelease}
            onClick={async () => {
              setActionError(null)
              setActionSuccess(null)
              setIsActing(true)
              try {
                await releaseReport(report.report_id)
                setActionSuccess('Report released.')
                await reportQuery.refetch()
              } catch (err) {
                setActionError((err as Error).message)
              } finally {
                setIsActing(false)
              }
            }}
          >
            Approve and Release
          </button>
        </div>
        {!canRelease && (
          <p className="mt-2 text-xs text-slate-500">
            Release is state-aware and role-restricted: only Admin users can release draft reports from this workbench.
          </p>
        )}
      </section>

      {actionError && <p className="text-sm text-red-600">{actionError}</p>}
      {actionSuccess && <p className="text-sm text-emerald-700">{actionSuccess}</p>}
      <Link to="/reviewer" className="inline-block text-sm underline">
        Back to reviewer queue
      </Link>
    </div>
  )
}
